import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spin, Typography, Alert, Skeleton } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { getPSPDFBaseUrl, getPSPDFLicenseKey } from '../viewer/pspdfkitConfig';
import './autoSplit.scss';

const { Text } = Typography;
let pspdfkitPromise;

const loadPSPDFKit = async () => {
  if (!pspdfkitPromise) {
    pspdfkitPromise = import(
      /* webpackChunkName: "viewer-pspdf-runtime" */
      'pspdfkit'
    ).catch((error) => {
      pspdfkitPromise = null;
      throw error;
    });
  }

  const module = await pspdfkitPromise;
  return module.default || module;
};

/**
 * PDF preview component using PSPDFKit to show only specific pages.
 * Accepts a pre-fetched ArrayBuffer to avoid re-downloading the PDF on each render.
 */
const AutoSplitPDFPreview = ({
  pdfArrayBuffer,
  pages,
  height,
}) => {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);
  const loadingRef = useRef(false);
  const pspdfkitRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pdfArrayBuffer || !containerRef.current) return;

    // Prevent concurrent loads
    if (loadingRef.current) return;

    const container = containerRef.current;
    let cancelled = false;
    let PSPDFKitModule = null;

    const loadDocument = async () => {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // Unload any existing instance - try both methods
      if (instanceRef.current) {
        try {
          pspdfkitRef.current?.unload(instanceRef.current);
        } catch (e) {
          // Ignore
        }
        instanceRef.current = null;
      }

      // Also try to unload by container in case instance ref was lost
      try {
        pspdfkitRef.current?.unload(container);
      } catch (e) {
        // Ignore - container might not have an instance
      }

      if (cancelled) return;

      try {
        PSPDFKitModule = await loadPSPDFKit();
        pspdfkitRef.current = PSPDFKitModule;
        const PSPDFKit = PSPDFKitModule;

        const initialViewState = new PSPDFKit.ViewState({
          layoutMode: PSPDFKit.LayoutMode.SINGLE,
        });

        // Create a copy of the ArrayBuffer because PSPDFKit transfers (detaches) it on load
        // Without this copy, switching splits would fail with "Cannot perform ArrayBuffer.prototype.slice on a detached ArrayBuffer"
        const pdfCopy = pdfArrayBuffer.slice(0);

        const config = {
          // Use ArrayBuffer directly - much faster than URL since data is already in memory
          document: pdfCopy,
          container,
          licenseKey: getPSPDFLicenseKey(),
          baseUrl: getPSPDFBaseUrl(),
          initialViewState,
          toolbarItems: [
            { type: 'sidebar-thumbnails' },
            { type: 'pager' },
            { type: 'zoom-out' },
            { type: 'zoom-in' },
            { type: 'zoom-mode' },
          ],
          disableTextSelection: true,
          locale: 'en',
          theme: PSPDFKit.Theme.LIGHT,
        };

        const instance = await PSPDFKit.load(config);

        // Check if cancelled during load
        if (cancelled) {
          try {
            PSPDFKitModule?.unload(instance);
          } catch (e) {
            // Ignore
          }
          return;
        }

        instanceRef.current = instance;

        // If specific pages are provided, remove all other pages
        if (pages && pages.length > 0 && pages.length < instance.totalPageCount) {
          // Build list of page indices to remove (0-indexed)
          const pagesToKeep = new Set(pages.map(p => p - 1)); // Convert to 0-indexed
          const pagesToRemove = [];

          for (let i = 0; i < instance.totalPageCount; i++) {
            if (!pagesToKeep.has(i)) {
              pagesToRemove.push(i);
            }
          }

          // Remove pages (must be done in descending order to preserve indices)
          if (pagesToRemove.length > 0 && !cancelled) {
            pagesToRemove.sort((a, b) => b - a); // Descending order

            for (const pageIndex of pagesToRemove) {
              if (cancelled) break;
              try {
                await instance.applyOperations([
                  {
                    type: 'removePages',
                    pageIndexes: [pageIndex],
                  },
                ]);
              } catch (e) {
                // Instance may have been unloaded, stop
                break;
              }
            }
          }

          // Go to first page
          if (!cancelled && instanceRef.current) {
            try {
              instance.setViewState(vs => vs.set('currentPageIndex', 0));
            } catch (e) {
              // Ignore if instance was unloaded
            }
          }
        }

        if (!cancelled) {
          setLoading(false);
        }
        loadingRef.current = false;
      } catch (err) {
        if (!cancelled) {
          console.error('[AutoSplitPDFPreview] Error loading PSPDFKit:', err);
          setError(err.message || 'Failed to load preview');
          setLoading(false);
        }
        loadingRef.current = false;
      }
    };

    loadDocument();

    return () => {
      cancelled = true;
      loadingRef.current = false;
      if (instanceRef.current) {
        try {
          pspdfkitRef.current?.unload(instanceRef.current);
        } catch (e) {
          // Ignore
        }
      }
      // Also try container
      try {
        pspdfkitRef.current?.unload(container);
      } catch (e) {
        // Ignore
      }
      instanceRef.current = null;
    };
  }, [pdfArrayBuffer, pages?.join(',')]); // Use pages as string to avoid reference issues

  if (!pdfArrayBuffer) {
    return (
      <div className="auto-split-pdf-no-url" style={{ height }}>
        <FileTextOutlined className="auto-split-page-icon" />
        <Text type="secondary">Document preview not available</Text>
      </div>
    );
  }

  return (
    <div className="auto-split-pdf-preview-container" style={{ height }}>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ margin: 12, borderRadius: 6 }}
        />
      )}
      <div className="auto-split-pdf-viewer-wrapper">
        {loading && (
          <div className="auto-split-pdf-loading">
            <div className="auto-split-pdf-skeleton">
              <div className="skeleton-toolbar">
                <Skeleton.Button active size="small" style={{ width: 32, marginRight: 8 }} />
                <Skeleton.Button active size="small" style={{ width: 60, marginRight: 8 }} />
                <Skeleton.Button active size="small" style={{ width: 32, marginRight: 8 }} />
                <Skeleton.Button active size="small" style={{ width: 32 }} />
              </div>
              <div className="skeleton-document">
                <Skeleton active paragraph={{ rows: 12 }} title={{ width: '60%' }} />
              </div>
              <Spin size="small" style={{ marginTop: 16 }} />
              <Text type="secondary" style={{ marginTop: 8 }}>Loading preview...</Text>
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          className="auto-split-pdf-container"
          style={{ height: '100%', display: loading ? 'none' : 'block' }}
        />
      </div>
    </div>
  );
};

AutoSplitPDFPreview.propTypes = {
  pdfArrayBuffer: PropTypes.instanceOf(ArrayBuffer),
  pages: PropTypes.arrayOf(PropTypes.number),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AutoSplitPDFPreview.defaultProps = {
  pdfArrayBuffer: null,
  pages: null,
  height: '400px',
};

export default AutoSplitPDFPreview;
